<script setup>
import { useMeta } from 'quasar';
import { useAppStore } from 'stores/app-store';
import { computed, defineAsyncComponent, ref, watch } from 'vue';

const appStore = useAppStore();

// Preload the service dialogs in case of a problem, as they may not be dynamically loadable during an outage.
import('components/service-dialog/NewVersionServiceDialog.vue');
import('components/service-dialog/UnavailableServiceDialog.vue');

const routerViewKey = ref(0);

const serviceDialogIsActive = computed(() => !!serviceDialogComponent.value);

const serviceDialogComponent = computed(() => {
    if (!!appStore.unavailableServiceDialog) return defineAsyncComponent(() => import('components/service-dialog/UnavailableServiceDialog.vue'));
    if (!!appStore.newVersionServiceDialog) return defineAsyncComponent(() => import('components/service-dialog/NewVersionServiceDialog.vue'));
    return null;
});

const serviceDialogProps = computed(() => {
    return !!appStore.unavailableServiceDialog
        ? appStore.unavailableServiceDialog ?? {}
        : {};
});

function appendMaterialSymbolsSharpLoadedClass() {
    if (!process.env.CLIENT) {
        return;
    }
    document.documentElement.classList.add('material-symbols-rounded-loaded');
}

async function watchMaterialSymbolsSharpLoaded() {
    if (!process.env.CLIENT) {
        return;
    }
    // As the icons are loaded from a third party source, the user may experience FOUT (Flash of Un-styled Text) which
    // displays the underlying text within the icon space before the font has loaded. This hack improves this.
    const FontFaceObserver = (await import('fontfaceobserver'))?.default;
    if (!FontFaceObserver) {
        // If for some reason the library cannot be loaded, then append the class anyway to ensure the user is not left
        // without icons.
        appendMaterialSymbolsSharpLoadedClass();
        return;
    }
    const font = new FontFaceObserver('Material Symbols Rounded');
    font.load().then(() => appendMaterialSymbolsSharpLoadedClass());
}

watchMaterialSymbolsSharpLoaded();

watch(serviceDialogIsActive, (newVal, oldVal) => {
    if (newVal === false && oldVal === true) {
        routerViewKey.value = routerViewKey.value + 1;
    }
});

useMeta({
    titleTemplate: title => title ? `${title} | Xprt Jobs` : 'Xprt Jobs'
});
</script>

<template>
    <router-view :key="routerViewKey"/>
    <component v-if="serviceDialogIsActive" :is="serviceDialogComponent" v-model="serviceDialogIsActive" v-bind="serviceDialogProps"/>
</template>
